import React, { useState, useEffect } from "react";
import { Modal, Button, Dropdown, FileInput, Radio } from "flowbite-react";
import { AiOutlineLoading } from "react-icons/ai";
import { timestampToDate } from "../../config/firebase";

const taskTypes = ["pdf", "image", "text", "conference"];
const taskStatuses = ["accepted", "rejected", "closed"];

const formatDateToInput = (date) => {
  if (!date) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0"); // Months are 0-based
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const EditTaskModal = ({
  task,
  isOpen,
  onClose,
  onSave,
  isLoading,
  isTemplate = false,
  templates = [],
}) => {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [type, setType] = useState(taskTypes[0]);
  const [status, setStatus] = useState(taskStatuses[0]);
  const [comment, setComment] = useState(null);
  const [deadline, setDeadline] = useState(null);
  const [attachment, setAttachment] = useState(null);
  const [attachmentType, setAttachmentType] = useState("url");

  const [createMode, setCreateMode] = useState("single");
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);

  // Update state when the task changes
  useEffect(() => {
    if (task) {
      setTitle(task.title || "");
      setDescription(task.description || "");
      setType(task.type || taskTypes[0]);
      setStatus(task.status || taskStatuses[0]);
      setComment(task.comment || "");
      setDeadline(formatDateToInput(timestampToDate(task.deadline)));
      setAttachment(task.attachment);
      setAttachmentType(task.attachmentType || "url");
      setCreateMode("single");
    }
  }, [task]);

  // Reset state when modal closes
  const handleClose = () => {
    onClose();
  };

  const handleSave = (e) => {
    e.preventDefault();

    if (createMode === "template") {
      if (!selectedTemplateId) return;
      onSave({ templateId: selectedTemplateId });
      return;
    }

    const updatedTask = {
      ...task,
      title,
      description,
      type,
      status,
      comment,
      deadline,
      attachment,
    };
    onSave(updatedTask);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setAttachment(file);
  };

  return (
    <Modal show={isOpen} onClose={handleClose} size="2xl">
      <Modal.Header>
        {task && task.id ? "Edit Task" : "Create Task"}
      </Modal.Header>
      <form onSubmit={handleSave}>
        <Modal.Body className="space-y-4 max-h-[60vh] overflow-y-auto">
          {isTemplate === false && !task?.id && (
            <div className="flex space-x-8">
              <div className="flex items-center gap-2">
                <Radio
                  id="single"
                  name="createMode"
                  value="single"
                  checked={createMode === "single"}
                  onClick={() => setCreateMode("single")}
                />
                <label
                  htmlFor="single"
                  className="text-sm text-gray-700 dark:text-gray-200"
                >
                  Single Task
                </label>
              </div>
              <div className="flex items-center gap-2">
                <Radio
                  id="template"
                  name="createMode"
                  value="template"
                  checked={createMode === "template"}
                  onClick={() => setCreateMode("template")}
                />
                <label
                  htmlFor="template"
                  className="text-sm text-gray-700 dark:text-gray-200"
                >
                  From Template
                </label>
              </div>
            </div>
          )}

          {createMode === "template" && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                Select Template
              </label>
              <select
                value={selectedTemplateId || ""}
                onChange={(e) => setSelectedTemplateId(e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                required
              >
                <option value="" disabled>
                  Select a template
                </option>
                {templates.map((template) => (
                  <option key={template.id} value={template.id}>
                    {template.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {createMode === "single" && (
            <>
              {/* Title */}
              <div>
                <label
                  htmlFor="title"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-200"
                >
                  Title
                </label>
                <input
                  id="title"
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  required
                />
              </div>

              {/* Description */}
              <div>
                <label
                  htmlFor="description"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-200"
                >
                  Description
                </label>
                <textarea
                  id="description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  rows={3}
                  required
                />
              </div>

              {/* Type */}
              <div>
                <label
                  htmlFor="type"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-200"
                >
                  Type
                </label>
                <Dropdown label={type.toUpperCase()} color="purple" size="sm">
                  {taskTypes.map((taskType) => (
                    <Dropdown.Item
                      key={taskType}
                      onClick={() => setType(taskType)}
                    >
                      {taskType}
                    </Dropdown.Item>
                  ))}
                </Dropdown>
              </div>

              {/* Status */}
              {task && task.id && isTemplate === false && (
                <div>
                  <label
                    htmlFor="status"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-200"
                  >
                    Status
                  </label>
                  <Dropdown
                    label={status.replace("_", " ").toUpperCase()}
                    color="purple"
                    size="sm"
                  >
                    {taskStatuses.map((taskStatus) => (
                      <Dropdown.Item
                        key={taskStatus}
                        onClick={() => setStatus(taskStatus)}
                      >
                        {taskStatus.replace("_", " ")}
                      </Dropdown.Item>
                    ))}
                  </Dropdown>
                </div>
              )}

              {/* Deadline */}
              {isTemplate === false && (
                <div>
                  <label
                    htmlFor="deadline"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-200"
                  >
                    Deadline (optional)
                  </label>
                  <input
                    id="deadline"
                    type="date"
                    value={deadline}
                    min={new Date().toISOString().split("T")[0]}
                    onChange={(e) => setDeadline(e.target.value)}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  />
                </div>
              )}

              {/* Attachment Type */}
              <div className="flex items-bottom space-x-2 content-end">
                <div className="w-full">
                  <label
                    htmlFor="attachment"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-200"
                  >
                    Attachment (optional)
                  </label>
                  <div className="flex items-center space-x-2 w-">
                    <Dropdown
                      label={attachmentType.toUpperCase()}
                      color="purple"
                      size="sm"
                    >
                      {["url", "file"].map((type) => (
                        <Dropdown.Item
                          key={type}
                          onClick={() => setAttachmentType(type)}
                        >
                          {type}
                        </Dropdown.Item>
                      ))}
                    </Dropdown>
                    {/* Conditional Attachment Field */}
                    {attachmentType === "file" ? (
                      <FileInput
                        id="attachment"
                        onChange={handleFileChange}
                        accept="image/*,application/pdf"
                        className="mt-1 block w-full text-sm text-gray-900 border border-gray-300 rounded-lg cursor-pointer bg-gray-50 dark:text-gray-400 focus:outline-none dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400"
                      />
                    ) : (
                      <input
                        id="attachmentText"
                        type="url"
                        value={attachment || ""}
                        onChange={(e) => setAttachment(e.target.value)}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                        rows={3}
                      />
                    )}
                  </div>
                </div>
              </div>

              {/* Comment */}
              <div>
                <label
                  htmlFor="comment"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-200"
                >
                  Comment (optional)
                </label>
                <textarea
                  id="comment"
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  rows={2}
                />
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button color="gray" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            color="purple"
            type="submit"
            disabled={isLoading}
            isProcessing={isLoading}
            processingSpinner={
              <AiOutlineLoading className="h-6 w-6 animate-spin" />
            }
          >
            {task && task.id ? "Update" : "Create"}
          </Button>
        </Modal.Footer>
      </form>
    </Modal>
  );
};

export default EditTaskModal;
